import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  LinearProgress,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { openAppConfirm } from "../../components/AppDialogs";
import { useWebPermissions } from "../../hooks/useWebPermissions";
import {
  CreateUsmleQuestionCall,
  DeleteUsmleQuestionCall,
  GenerateUsmleQuestionsCall,
  GetUsmleCatalogCall,
  GetUsmleQuestionsCall,
  SaveGeneratedUsmleQuestionsCall,
  UpdateUsmleQuestionCall,
} from "../../redux/api/usmlePrep.api";

const EMPTY_CHOICES = ["a", "b", "c", "d", "e"].map((key) => ({
  key,
  text: "",
  is_correct: key === "a",
}));

const FALLBACK_STEPS = [
  {
    id: "step_1",
    label: "Step 1",
    subjects: [
      { id: "anatomy", label: "Anatomy" },
      { id: "histology", label: "Histology" },
      { id: "physiology", label: "Physiology" },
      { id: "biochemistry", label: "Biochemistry" },
      { id: "pathology", label: "Pathology" },
      { id: "pharmacology", label: "Pharmacology" },
      { id: "microbiology", label: "Microbiology" },
      { id: "immunology", label: "Immunology" },
      { id: "behavioral", label: "Behavioral science" },
    ],
  },
  {
    id: "step_2_ck",
    label: "Step 2 CK",
    subjects: [
      { id: "internal_medicine", label: "Internal medicine" },
      { id: "surgery", label: "Surgery" },
      { id: "pediatrics", label: "Pediatrics" },
      { id: "obgyn", label: "Obstetrics & gynecology" },
      { id: "psychiatry", label: "Psychiatry" },
      { id: "neurology", label: "Neurology" },
      { id: "emergency", label: "Emergency medicine" },
      { id: "preventive", label: "Preventive medicine" },
    ],
  },
  {
    id: "step_3",
    label: "Step 3",
    subjects: [
      { id: "internal_medicine", label: "Internal medicine" },
      { id: "surgery", label: "Surgery" },
      { id: "pediatrics", label: "Pediatrics" },
      { id: "obgyn", label: "Obstetrics & gynecology" },
      { id: "psychiatry", label: "Psychiatry" },
      { id: "emergency", label: "Emergency medicine" },
      { id: "ambulatory", label: "Ambulatory care" },
    ],
  },
];

const MAX_AI_QUESTIONS = 150;
const AI_BATCH_SIZE = 10;

const emptyForm = (step = "step_1", subject = "anatomy") => ({
  id: null,
  step,
  subject,
  stem: "",
  explanation: "",
  difficulty: "medium",
  is_active: true,
  choices: EMPTY_CHOICES.map((c) => ({ ...c })),
});

function QuestionReviewer({
  items,
  index,
  onIndex,
  subjects,
  teal,
  isDark,
  divider,
  paper,
  emptyText,
  extra,
}) {
  const total = items.length;
  const item = items[index] || null;
  const canPrev = index > 0;
  const canNext = index < total - 1;

  const go = (nextIndex) => {
    if (nextIndex < 0 || nextIndex >= total) return;
    onIndex(nextIndex);
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.25 },
        borderRadius: 2,
        border: `1px solid ${divider}`,
        bgcolor: paper,
      }}
    >
      {total ? (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <IconButton
              onClick={() => go(index - 1)}
              disabled={!canPrev}
              aria-label="Previous question"
              sx={{
                border: `1px solid ${divider}`,
                bgcolor: paper,
                width: 44,
                height: 44,
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
              <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap" useFlexGap>
                {items.map((entry, i) => {
                  const passed = i < index;
                  const current = i === index;
                  return (
                    <Box
                      key={entry.id || i}
                      component="button"
                      type="button"
                      onClick={() => go(i)}
                      title={passed ? "Reviewed" : current ? "Current" : `Question ${i + 1}`}
                      aria-label={passed ? `Question ${i + 1}, reviewed` : `Question ${i + 1}`}
                      sx={{
                        width: 30,
                        height: 30,
                        flex: "0 0 auto",
                        borderRadius: "50%",
                        border: current ? `2px solid ${teal}` : "1px solid transparent",
                        bgcolor: passed ? "#16a34a" : current ? teal : isDark ? "#334155" : "#e2e8f0",
                        color: passed || current ? "#fff" : isDark ? "#cbd5e1" : "#334155",
                        fontSize: 12,
                        fontWeight: 800,
                        cursor: "pointer",
                        lineHeight: 1,
                        p: 0,
                        appearance: "none",
                      }}
                    >
                      {i + 1}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
            <IconButton
              onClick={() => go(index + 1)}
              disabled={!canNext}
              aria-label="Next question"
              sx={{
                border: `1px solid ${divider}`,
                bgcolor: paper,
                width: 44,
                height: 44,
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Question {index + 1} of {total}
              {canNext ? " — use arrows or keyboard ← →" : " — end of list"}
            </Typography>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#16a34a" }} />
              <Typography variant="caption" color="text.secondary">
                Reviewed
              </Typography>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: teal }} />
              <Typography variant="caption" color="text.secondary">
                Current
              </Typography>
            </Stack>
          </Stack>

          {item ? (
            <>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
                <Chip
                  size="small"
                  label={subjects.find((s) => s.id === item.subject)?.label || item.subject}
                />
                {item.difficulty ? <Chip size="small" label={item.difficulty} /> : null}
                {item.is_active === false ? <Chip size="small" color="warning" label="inactive" /> : null}
              </Stack>
              <Typography variant="h6" fontWeight={700} sx={{ whiteSpace: "pre-wrap", mb: 1.75 }}>
                {item.stem}
              </Typography>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {(item.choices || []).map((choice) => {
                  const correct = !!choice.is_correct;
                  return (
                    <Box
                      key={choice.key}
                      sx={{
                        p: 1.2,
                        borderRadius: 1.5,
                        border: `1px solid ${correct ? "#16a34a" : divider}`,
                        bgcolor: correct
                          ? isDark
                            ? "rgba(22,163,74,0.18)"
                            : "#ecfdf5"
                          : "transparent",
                      }}
                    >
                      <Typography variant="body2" fontWeight={correct ? 800 : 500} sx={{ color: correct ? "#15803d" : "text.primary" }}>
                        {String(choice.key || "").toUpperCase()}. {choice.text}
                        {correct ? "  ✓ correct" : ""}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
              {item.explanation ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: isDark ? "rgba(15,118,110,0.12)" : "#f0fdfa",
                    border: `1px solid ${isDark ? "rgba(45,212,191,0.25)" : "#99f6e4"}`,
                  }}
                >
                  <Typography variant="caption" fontWeight={800} sx={{ color: teal, display: "block", mb: 0.5 }}>
                    Explanation
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {item.explanation}
                  </Typography>
                </Box>
              ) : null}
              {extra ? <Box sx={{ mt: 2 }}>{extra(item)}</Box> : null}
            </>
          ) : null}
        </>
      ) : (
        <Typography color="text.secondary">{emptyText}</Typography>
      )}
    </Box>
  );
}

export default function UsmlePrepAdminPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const teal = isDark ? "#2DD4BF" : "#0F766E";
  const { can } = useWebPermissions();
  const canAdd = can("usmle", "add");
  const canEdit = can("usmle", "edit");
  const canDelete = can("usmle", "delete");
  const canGenerate = can("usmle", "generate");
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState("step_1");
  const [subject, setSubject] = useState("");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiCount, setAiCount] = useState(5);
  const [aiTopic, setAiTopic] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiGoal, setAiGoal] = useState(0);
  const [drafts, setDrafts] = useState([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [draftIndex, setDraftIndex] = useState(0);
  const generateAbortRef = useRef(false);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: isDark ? theme.palette.grey?.[800] ?? "#2d2d2d" : theme.palette.background.paper,
      color: theme.palette.text.primary,
      "& fieldset": { borderColor: theme.palette.divider },
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
  };
  const selectSx = {
    ...inputSx,
    "& .MuiOutlinedInput-root": {
      ...inputSx["& .MuiOutlinedInput-root"],
      minHeight: 40,
      height: 40,
    },
    "& .MuiSelect-select": {
      minHeight: "40px !important",
      height: "40px !important",
      display: "flex !important",
      alignItems: "center",
      padding: "8px 36px 8px 12px !important",
      boxSizing: "border-box",
    },
    "& .MuiSelect-icon": {
      display: "inline-flex !important",
      right: 8,
    },
  };
  const selectMenuProps = {
    PaperProps: {
      sx: {
        maxHeight: 280,
        mt: 0.5,
        "& .MuiMenuItem-root": { minHeight: 36, py: 0.75 },
      },
    },
  };

  const steps = useMemo(() => {
    const remote = Array.isArray(catalog?.steps) ? catalog.steps : [];
    return FALLBACK_STEPS.map((local) => {
      const found = remote.find((s) => s.id === local.id);
      const remoteSubjects = Array.isArray(found?.subjects) ? found.subjects : [];
      const subjects = local.subjects.map((sub) => {
        const match = remoteSubjects.find((r) => r.id === sub.id);
        return {
          ...sub,
          label: match?.label || sub.label,
          question_count: Number(match?.question_count) || 0,
        };
      });
      return {
        ...local,
        label: found?.label || local.label,
        question_count: found?.question_count,
        subjects,
      };
    });
  }, [catalog]);
  const stepMeta = useMemo(() => steps.find((s) => s.id === step) || steps[0], [steps, step]);
  const subjects = stepMeta?.subjects || [];
  const visibleItems = useMemo(() => {
    if (!subject) return items;
    return items.filter((item) => item.subject === subject);
  }, [items, subject]);
  const selectedTopicLabel =
    subjects.find((s) => s.id === subject)?.label || (subject ? subject : "all topics");

  const loadCatalog = useCallback(async () => {
    const res = await GetUsmleCatalogCall();
    if (res?.status === 200) setCatalog(res.data);
  }, []);

  const loadQuestions = useCallback(async () => {
    const res = await GetUsmleQuestionsCall({
      step,
      subject: subject || undefined,
    });
    if (res?.status === 200 && Array.isArray(res.data)) setItems(res.data);
    else setItems([]);
  }, [step, subject]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    setReviewIndex(0);
  }, [step, subject]);

  useEffect(() => {
    setReviewIndex((i) => Math.min(i, Math.max(visibleItems.length - 1, 0)));
  }, [visibleItems.length]);

  useEffect(() => {
    const onKey = (event) => {
      const tag = String(event.target?.tagName || "");
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;
      if (aiOpen) {
        if (event.key === "ArrowLeft") setDraftIndex((i) => Math.max(0, i - 1));
        if (event.key === "ArrowRight") setDraftIndex((i) => Math.min(Math.max(drafts.length - 1, 0), i + 1));
        return;
      }
      if (event.key === "ArrowLeft") setReviewIndex((i) => Math.max(0, i - 1));
      if (event.key === "ArrowRight") setReviewIndex((i) => Math.min(Math.max(visibleItems.length - 1, 0), i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aiOpen, drafts.length, visibleItems.length]);

  useEffect(() => {
    const first = FALLBACK_STEPS.find((s) => s.id === step)?.subjects?.[0]?.id || "anatomy";
    setForm(emptyForm(step, first));
  }, [step]);

  const onEdit = (item) => {
    setForm({
      id: item.id,
      step: item.step,
      subject: item.subject,
      stem: item.stem,
      explanation: item.explanation || "",
      difficulty: item.difficulty || "medium",
      is_active: item.is_active !== false,
      choices:
        (item.choices || []).length >= 2
          ? ["a", "b", "c", "d", "e"].map((key, i) => {
              const found = (item.choices || []).find((c) => c.key === key) || item.choices[i];
              return {
                key,
                text: found?.text || "",
                is_correct: !!found?.is_correct,
              };
            })
          : EMPTY_CHOICES.map((c) => ({ ...c })),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSave = async () => {
    if (form.id && !canEdit) return;
    if (!form.id && !canAdd) return;
    setSaving(true);
    setMessage("");
    const body = {
      step: form.step,
      subject: form.subject,
      stem: form.stem,
      explanation: form.explanation,
      difficulty: form.difficulty,
      is_active: form.is_active,
      choices: form.choices,
    };
    const res = form.id
      ? await UpdateUsmleQuestionCall(form.id, body)
      : await CreateUsmleQuestionCall(body);
    setSaving(false);
    if (res?.status === 200 || res?.status === 201) {
      setMessage("Saved.");
      setForm(emptyForm(step, subject || subjects[0]?.id));
      loadQuestions();
      loadCatalog();
    } else {
      setMessage(res?.message || "Could not save.");
    }
  };

  const onDelete = async (item) => {
    if (!canDelete) return;
    const ok = await openAppConfirm({
      title: "Delete this USMLE question?",
      confirmText: "Delete",
    });
    if (!ok) return;
    const res = await DeleteUsmleQuestionCall(item.id);
    if (res?.status === 200) {
      loadQuestions();
      loadCatalog();
      if (form.id === item.id) setForm(emptyForm(step, subject || subjects[0]?.id));
    }
  };

  const generate = async () => {
    if (!canGenerate) return;
    generateAbortRef.current = false;
    const target = Math.max(1, Math.min(MAX_AI_QUESTIONS, Number(aiCount) || 5));
    setAiCount(target);
    setAiGoal(target);
    setAiBusy(true);
    setMessage("");
    setDrafts([]);
    setDraftIndex(0);

    const collected = [];
    const subjectId = form.subject || subjects[0]?.id;

    while (collected.length < target && !generateAbortRef.current) {
      const batch = Math.min(AI_BATCH_SIZE, target - collected.length);
      const avoid = collected
        .slice(-30)
        .map((q) => String(q.stem || "").replace(/\s+/g, " ").slice(0, 90))
        .filter(Boolean);
      const extra = [
        String(aiTopic || "").trim(),
        avoid.length
          ? `Do not repeat or closely paraphrase these already drafted questions: ${avoid.join(" || ")}`
          : "",
      ]
        .filter(Boolean)
        .join("\n");

      const res = await GenerateUsmleQuestionsCall({
        step,
        subject: subjectId,
        count: batch,
        topic: extra,
      });

      if (res?.status !== 200 || !Array.isArray(res.data) || !res.data.length) {
        setMessage(
          collected.length
            ? res?.message ||
                `Stopped after ${collected.length} of ${target}. You can save these or generate again.`
            : res?.message || "AI generation failed."
        );
        break;
      }

      const next = res.data.map((q, i) => ({
        ...q,
        id: `draft-${collected.length + i + 1}`,
      }));
      collected.push(...next);
      setDrafts([...collected]);
    }

    setAiBusy(false);
    if (generateAbortRef.current && collected.length) {
      setMessage(`Stopped at ${collected.length} of ${target}. You can save these drafts.`);
    }
  };

  const saveDrafts = async () => {
    if (!canAdd) return;
    const keepers = drafts.filter((d) => d.stem && d.explanation);
    if (!keepers.length) return;
    setAiBusy(true);
    const res = await SaveGeneratedUsmleQuestionsCall(keepers);
    setAiBusy(false);
    if (res?.status === 201 || res?.status === 200) {
      const topic = keepers[0]?.subject || form.subject || "";
      if (topic) setSubject(topic);
      setAiOpen(false);
      setDrafts([]);
      setAiTopic("");
      loadQuestions();
      loadCatalog();
      setMessage(`Saved ${keepers.length} AI question${keepers.length === 1 ? "" : "s"}.`);
    } else {
      setMessage(res?.message || "Could not save generated questions.");
    }
  };

  const updateDraft = (idx, patch) => {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Typography variant="h4" fontWeight={800} sx={{ color: teal }}>
          USMLE Prep
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Step 1 • Step 2 CK • Step 3 — generate with AI, edit, delete. Each student attempt is 10 questions with saved explanations.
        </Typography>

        <Tabs
          value={step}
          onChange={(_, v) => {
            setStep(v);
            setSubject("");
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2,
            minHeight: 42,
            width: "fit-content",
            maxWidth: "100%",
            "& .MuiTabs-flexContainer": { height: "auto", minHeight: 34 },
            "& .MuiTab-root": {
              minHeight: 34,
              flex: "0 0 auto",
              maxWidth: "none",
              py: 0.75,
              px: 2,
            },
            "& .Mui-selected": { color: `${teal} !important` },
          }}
        >
          {steps.map((s) => (
            <Tab
              key={s.id}
              value={s.id}
              label={`${s.label}${s.question_count != null ? ` (${s.question_count})` : ""}`}
            />
          ))}
        </Tabs>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 240, ...selectSx }}>
            <InputLabel>Filter by topic</InputLabel>
            <Select
              label="Filter by topic"
              value={subject}
              onChange={(e) => {
                const id = e.target.value;
                setSubject(id);
                if (id) setForm((p) => ({ ...p, subject: id }));
              }}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="">All topics</MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label} ({s.question_count || 0})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {canGenerate ? (
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => setAiOpen(true)}
              startIcon={<AutoAwesomeRoundedIcon />}
            >
              Generate with AI
            </MDButton>
          ) : null}
        </Stack>

        {message ? (
          <Typography variant="body2" sx={{ mb: 2, color: teal }}>
            {message}
          </Typography>
        ) : null}

        <Grid container spacing={2}>
          {canAdd || (form.id && canEdit) ? (
          <Grid item xs={12} lg={5}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
              }}
            >
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>
                {form.id ? "Edit question" : "New question"}
              </Typography>
              <Stack spacing={1.5}>
                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Topic</InputLabel>
                  <Select
                    label="Topic"
                    value={subjects.some((s) => s.id === form.subject) ? form.subject : subjects[0]?.id || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      setForm((p) => ({ ...p, subject: id }));
                      setSubject(id);
                    }}
                    MenuProps={selectMenuProps}
                  >
                    {subjects.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    label="Difficulty"
                    value={form.difficulty}
                    onChange={(e) => setForm((p) => ({ ...p, difficulty: e.target.value }))}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="easy">Easy</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="hard">Hard</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Stem"
                  multiline
                  minRows={4}
                  value={form.stem}
                  onChange={(e) => setForm((p) => ({ ...p, stem: e.target.value }))}
                  sx={inputSx}
                />
                {form.choices.map((choice, idx) => (
                  <Stack key={choice.key} direction="row" spacing={1} alignItems="flex-start">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!choice.is_correct}
                          onChange={() =>
                            setForm((p) => ({
                              ...p,
                              choices: p.choices.map((c, i) => ({
                                ...c,
                                is_correct: i === idx,
                              })),
                            }))
                          }
                        />
                      }
                      label={choice.key.toUpperCase()}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      value={choice.text}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          choices: p.choices.map((c, i) =>
                            i === idx ? { ...c, text: e.target.value } : c
                          ),
                        }))
                      }
                      sx={inputSx}
                    />
                  </Stack>
                ))}
                <TextField
                  label="Explanation (shown after the attempt)"
                  multiline
                  minRows={4}
                  value={form.explanation}
                  onChange={(e) => setForm((p) => ({ ...p, explanation: e.target.value }))}
                  sx={inputSx}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.is_active}
                      onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                    />
                  }
                  label="Active (included in student attempts)"
                />
                <Stack direction="row" spacing={1}>
                  {(form.id ? canEdit : canAdd) ? (
                    <MDButton variant="gradient" color="info" onClick={onSave} disabled={saving}>
                      {saving ? "Saving…" : form.id ? "Update" : "Create"}
                    </MDButton>
                  ) : null}
                  {form.id ? (
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => setForm(emptyForm(step, subject || subjects[0]?.id))}
                    >
                      Cancel
                    </MDButton>
                  ) : null}
                </Stack>
              </Stack>
            </Box>
          </Grid>
          ) : null}
          <Grid item xs={12} lg={canAdd || (form.id && canEdit) ? 7 : 12}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.25 }}>
              Review questions — {selectedTopicLabel} ({visibleItems.length})
            </Typography>
            <QuestionReviewer
              items={visibleItems}
              index={Math.min(reviewIndex, Math.max(visibleItems.length - 1, 0))}
              onIndex={setReviewIndex}
              subjects={subjects}
              teal={teal}
              isDark={isDark}
              divider={theme.palette.divider}
              paper={theme.palette.background.paper}
              emptyText={
                subject
                  ? `No ${selectedTopicLabel} questions yet. Generate with AI or add one on the left.`
                  : "No questions in this filter yet. Generate with AI or add one on the left."
              }
              extra={
                canEdit || canDelete
                  ? (item) => (
                      <Stack direction="row" spacing={1}>
                        {canEdit ? (
                          <MDButton size="small" color="info" onClick={() => onEdit(item)}>
                            Edit
                          </MDButton>
                        ) : null}
                        {canDelete ? (
                          <MDButton size="small" color="error" onClick={() => onDelete(item)}>
                            Delete
                          </MDButton>
                        ) : null}
                      </Stack>
                    )
                  : null
              }
            />
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={aiOpen}
        onClose={() => !aiBusy && setAiOpen(false)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{ sx: { maxHeight: "92vh" } }}
      >
        <DialogTitle>Generate USMLE questions with AI</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Drafts land in {stepMeta?.label || step} / {subjects.find((s) => s.id === (form.subject || subjects[0]?.id))?.label}. Review, edit, then save.
              Large banks (up to {MAX_AI_QUESTIONS}) are generated {AI_BATCH_SIZE} at a time.
            </Typography>
            <FormControl size="small" fullWidth sx={selectSx}>
              <InputLabel>Topic</InputLabel>
              <Select
                label="Topic"
                value={subjects.some((s) => s.id === form.subject) ? form.subject : subjects[0]?.id || ""}
                onChange={(e) => {
                  const id = e.target.value;
                  setForm((p) => ({ ...p, subject: id }));
                  setSubject(id);
                }}
                MenuProps={selectMenuProps}
              >
                {subjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              label="How many"
              value={aiCount}
              onChange={(e) =>
                setAiCount(Math.max(1, Math.min(MAX_AI_QUESTIONS, Number(e.target.value) || 1)))
              }
              inputProps={{ min: 1, max: MAX_AI_QUESTIONS }}
              helperText={`Up to ${MAX_AI_QUESTIONS}. 150 questions take several minutes.`}
              sx={inputSx}
            />
            <TextField
              label="Extra instruction (optional)"
              value={aiTopic}
              onChange={(e) => setAiTopic(e.target.value)}
              sx={inputSx}
            />
            {aiBusy ? (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={22} />
                  <Typography variant="body2">
                    Generating {drafts.length} of {aiGoal || aiCount}…
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.round((drafts.length / Math.max(aiGoal || aiCount, 1)) * 100)}
                />
              </Stack>
            ) : null}
            {drafts.length ? (
              <QuestionReviewer
                items={drafts}
                index={Math.min(draftIndex, Math.max(drafts.length - 1, 0))}
                onIndex={setDraftIndex}
                subjects={subjects}
                teal={teal}
                isDark={isDark}
                divider={theme.palette.divider}
                paper={theme.palette.background.paper}
                emptyText=""
                extra={(draft) => (
                  <Stack spacing={1.25}>
                    <TextField
                      fullWidth
                      multiline
                      label="Edit stem"
                      value={draft.stem}
                      onChange={(e) => updateDraft(draftIndex, { stem: e.target.value })}
                      sx={inputSx}
                    />
                    <TextField
                      fullWidth
                      multiline
                      label="Edit explanation"
                      value={draft.explanation}
                      onChange={(e) => updateDraft(draftIndex, { explanation: e.target.value })}
                      sx={inputSx}
                    />
                  </Stack>
                )}
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => {
              if (aiBusy) {
                generateAbortRef.current = true;
                return;
              }
              setAiOpen(false);
            }}
          >
            {aiBusy ? "Stop" : "Close"}
          </MDButton>
          {!drafts.length || aiBusy ? (
            <MDButton color="info" onClick={generate} disabled={aiBusy}>
              {aiBusy ? "Generating…" : "Generate"}
            </MDButton>
          ) : (
            <MDButton color="info" onClick={saveDrafts} disabled={aiBusy || !canAdd}>
              {aiBusy ? "Saving…" : `Save ${drafts.length}`}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}
